import * as api from "@/api";
import AiToolItem from "@/components/ai-tool-item";
import AiToolsFooter from "@/components/ai-tools-footer";
import styles from "@/components/ai-tools-popup.module.css";
import PopupShell from "@/components/popup-shell";
import { globals } from "@/globals";
import { closePopup, handleKeyboardClick } from "@/utils";
import {
	faBookOpen,
	faComments,
	faEarthAmericas,
	faFilePowerpoint,
	faIcons,
	faPalette,
	faPenNib,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import { t } from "i18next";
import { JSX, useState } from "react";

interface AiToolsPopupProps {
	isShown: boolean;
}

function AiToolsPopup({ isShown }: AiToolsPopupProps): JSX.Element {
	const [selectedCategory, setSelectedCategory] = useState<string>("all");

	const { data, isError } = useQuery({
		queryFn: api.getAiTools,
		queryKey: ["aiTools"],
		refetchOnMount: false,
		refetchOnWindowFocus: false,
	});

	const closeAiToolsPopup = (): void => {
		closePopup("aiTools");
	};

	const categories = [
		{
			icon: faIcons,
			name: "all",
		},
		{
			icon: faComments,
			name: "chat",
		},
		{
			icon: faPenNib,
			name: "writing",
		},
		{
			icon: faPalette,
			name: "design",
		},
		{
			icon: faFilePowerpoint,
			name: "ppt",
		},
		{
			icon: faEarthAmericas,
			name: "translate",
		},
		{
			icon: faBookOpen,
			name: "reading",
		},
	];

	const categoryElements = categories.map((category) => {
		const handleClick = (): void => {
			setSelectedCategory(category.name);
		};

		return (
			<div
				aria-selected={selectedCategory === category.name}
				className={styles["category-option"]}
				key={category.name}
				onClick={handleClick}
				onKeyDown={handleKeyboardClick(handleClick)}
				role="tab"
				tabIndex={0}
			>
				<FontAwesomeIcon icon={category.icon} />
				<span>{t(category.name)}</span>
			</div>
		);
	});

	const toolElements = data?.map((item) => {
		if (
			(item.isExtensionOnly && !globals.isExtension) ||
			(selectedCategory !== "all" &&
				!item.categories.includes(selectedCategory))
		) {
			return null;
		}

		return (
			<AiToolItem
				item={item}
				key={item.title}
			/>
		);
	});

	return (
		<PopupShell
			isShown={isShown}
			onClose={closeAiToolsPopup}
			title={t("aiTools")}
		>
			<div className={styles["subtitle"]}>{t("aiToolsSubtitle")}</div>
			<div className={styles["category-container"]}>
				{categoryElements}
			</div>
			<div className={styles["list"]}>
				{toolElements}
				{isError && (
					<div className={styles["error-info"]}>
						{t("failedToLoad")}
					</div>
				)}
			</div>
			<AiToolsFooter />
		</PopupShell>
	);
}

export default AiToolsPopup;
